import { msg } from "@lingui/core/macro";
import { createMemo } from "solid-js";
import { useI18n } from "@/providers/i18n-provider";
import { type ConnectivityContext, getConnectivityDescriptionKeys } from "@cashsync/shared";
import { getConnectivityUI } from "@fleet/powersync";

/**
 * Hook to get unified connectivity dialog content.
 * Centralizes labels and messages for all platforms.
 */
export function useConnectivityDialog(
  stateValue: () => string | object,
  context: () => ConnectivityContext,
) {
  const { t } = useI18n();
  const ui = createMemo(() => getConnectivityUI(stateValue(), context()));

  const dialogContent = createMemo(() => {
    const ctx = context();
    const keys = getConnectivityDescriptionKeys(ui().labelKey, ctx);

    const content = (() => {
      // Map shared keys to actual translated strings.
      // Note: These keys must be present in the i18n catalogs.
      switch (keys.titleKey) {
        case "connectivity.offline.title": {
          return {
            title: t(msg`You're Offline`),
            message: t(msg`Check your connection to sync your changes.`),
            confirmText: t(msg`Check Again`),
          };
        }
        case "connectivity.reconnecting.title": {
          return {
            title: t(msg`Server Offline`),
            message: t(msg`We're having trouble reaching the server. We'll keep trying...`),
            confirmText: t(msg`Check Again`),
          };
        }
        case "connectivity.maintenance.title": {
          return {
            title: t(msg`Maintenance`),
            message: ctx.maintenanceMessage ?? t(msg`The server is down for maintenance.`),
            confirmText: t(msg`Check Again`),
          };
        }
        // Write-only window: everything the user can SEE still works, so the
        // copy says what is actually happening (changes are queued) instead of
        // "the server is down", which would be false.
        case "connectivity.maintenance_writes.title": {
          return {
            title: t(msg`Saving paused`),
            message:
              ctx.maintenanceMessage ??
              t(
                msg`We're doing maintenance. Your changes are saved on this device and will sync when it's done.`,
              ),
            confirmText: t(msg`OK`),
          };
        }
        case "connectivity.unauthenticated.title": {
          return {
            title: t(msg`Visitor Mode`),
            message: t(
              msg`Sign in or create an account to sync your tasks and collaborate with others.`,
            ),
            confirmText: t(msg`Sign In`),
            secondaryConfirmText: t(msg`Create Account`),
            actionType: "auth" as const,
          };
        }
        case "connectivity.checking.title": {
          return {
            title: t(msg`Checking Connection`),
            message: t(
              msg`Verifying your connectivity status. This happens automatically in the background.`,
            ),
            hideConfirm: true,
            cancelText: t(msg`Close`),
          };
        }
        case "connectivity.syncing.title": {
          return {
            title: t(msg`Syncing Changes`),
            message: t(msg`Your work is being saved to the cloud.`),
            confirmText: t(msg`OK`),
          };
        }
        case "connectivity.pending.title": {
          const countValue = ctx.uploadQueueSize;
          return {
            title: t(msg`Pending Changes`),
            message: t(msg`You have ${countValue} changes waiting to be synced.`),
            confirmText: t(msg`Sync Now`),
          };
        }
        default: {
          return {
            title: t(msg`You're Connected`),
            message: t(msg`Your data is up to date and synced to the cloud.`),
            confirmText: t(msg`Check Again`),
          };
        }
      }
    })();

    return content;
  });

  return dialogContent;
}
